"use client";

import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { renderUiNode } from "./atoms/render-ui-node";
import type { NavTarget } from "../context/execution-context";
import type { StudioScreenRendererProps } from "./StudioScreenRenderer.props";
import { useAtomContext } from "./StudioScreenRenderer.atoms";
import { resolveAppLocaleDir, useScreenBootstrap } from "./StudioScreenRenderer.bootstrap";
import { useScreenExecution } from "./StudioScreenRenderer.execution";
import { useDocumentMetadataSync } from "./StudioScreenRenderer.head";
import { usePrePaintEffect } from "./StudioScreenRenderer.pre-paint";
import { useScreenRoute } from "./StudioScreenRenderer.route";
import { useScreenState } from "./StudioScreenRenderer.state";
import { useScreenTriggers } from "./StudioScreenRenderer.triggers";
import {
 buildLocalePathMap,
 buildLocalePathname,
 extractLocalePrefix,
 pickLocaleRecord,
} from "./StudioScreenRenderer.utils.locale-route";
import { consoleLogger } from "./StudioScreenRenderer.utils";

const BROWSER_NAVIGATION_EVENT = "studio-runtime:navigate";

const navigationBookkeeping = {
 hasCommittedScreen: false,
 lastPopStateAt:0,
};

const HISTORY_NAV_WINDOW_MS =1_000;

if (typeof window !== "undefined") {
 window.addEventListener("popstate", () => {
 navigationBookkeeping.lastPopStateAt = Date.now();
 });
}

function scrollToDocumentTop(): void {
 window.scrollTo(0,0);
}

function stableKey(value: unknown): string {
 if (value === null || value === undefined) return "null";
 if (Array.isArray(value)) {
 return `[${value.map((item) => stableKey(item)).join(",")}]`;
 }
 if (typeof value === "object") {
 const record = value as Record<string, unknown>;
 return `{${Object.keys(record)
 .sort()
 .map(
 (key) => `${JSON.stringify(key)}:${stableKey(record[key])}`,
 )
 .join(",")}}`;
 }
 return JSON.stringify(value);
}

function buildInternalNavigationUrl(
 app: StudioScreenRendererProps["app"],
 target: NavTarget,
): string | null {
 const requestedTarget = String(target.to ?? target.url ?? "").trim();
 if (!requestedTarget) return null;

 let pathname = requestedTarget;
 if (target.kind === "screen" && !pathname.startsWith("/")) {
 const configuredPath = app.config.routes?.[pathname];
 const screenId = app.screens[pathname]
 ? pathname
 : app.slugIndex[pathname];
 const screenSlug = screenId
 ? app.screens[screenId]?.manifest.slug
 : pathname;
 pathname = configuredPath ?? app.config.routes?.[screenSlug ?? ""] ?? `/${screenSlug ?? pathname}`;
 }

 if (!pathname.startsWith("/")) pathname = `/${pathname}`;

 const locales = app.i18n?.locales ?? [];
 const currentUrlLocale =
 typeof window !== "undefined"
 ? extractLocalePrefix(window.location.pathname, locales)?.locale
 : undefined;
 if (currentUrlLocale) {
 pathname = buildLocalePathname(pathname, currentUrlLocale, locales);
 }

 const params = target.params ?? {};
 const consumedParams = new Set<string>();
 pathname = pathname.replace(/\[([^\]]+)\]|:([A-Za-z0-9_]+)/g, (match, bracketName: string | undefined, colonName: string | undefined) => {
 const name = bracketName ?? colonName;
 if (!name || !Object.prototype.hasOwnProperty.call(params, name)) return match;

 const value = params[name];
 if (value === null || value === undefined) return match;
 consumedParams.add(name);
 return encodeURIComponent(String(value));
 });

 try {
 const url = new URL(pathname, window.location.origin);
 if (url.origin !== window.location.origin) return null;

 for (const [key, value] of Object.entries(params)) {
 if (consumedParams.has(key) || value === null || value === undefined) continue;
 const values = Array.isArray(value) ? value : [value];
 for (const item of values) {
 if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
 url.searchParams.append(key, String(item));
 }
 }
 }

 return `${url.pathname}${url.search}${url.hash}`;
 } catch {
 return null;
 }
}

export default function StudioScreenRenderer(
 props: StudioScreenRendererProps,
): React.ReactNode {
 const browserNavigate = useCallback((target: NavTarget) => {
 if (typeof window === "undefined") return;

 if (target.kind === "back") {
 window.history.back();
 return;
 }

 if (target.kind === "external") {
 const externalUrl = String(target.url ?? target.to ?? "").trim();
 if (externalUrl) window.location.assign(externalUrl);
 return;
 }

 const url = buildInternalNavigationUrl(props.app, target);
 if (!url) return;

 if (target.replace) {
 window.history.replaceState(window.history.state, "", url);
 } else {
 window.history.pushState(window.history.state, "", url);
 }

 scrollToDocumentTop();

 window.dispatchEvent(new Event(BROWSER_NAVIGATION_EVENT));
 }, [props.app]);
 const navigate = props.navigate ?? browserNavigate;
 const {
 screen,
 route,
 translations: routeTranslations,
 browserLocation,
 isHistoryNavigation,
 locale: urlLocale,
 requestLocale,
 } = useScreenRoute(props);
 const { store, formRef, setFormValue, getFormValues } = useScreenState();
 const logger = props.logger ?? consoleLogger;
 const { transport, executionDeps, runFlow, auth, storage } = useScreenExecution({
 app: props.app,
 store,
 route,
 screen,
 formRef,
 navigate,
 storage: props.storage,
 auth: props.auth,
 logger,
 operationsBaseUrl: props.operationsBaseUrl,
 });
 const fireTrigger = useScreenTriggers(executionDeps, runFlow);
 const stateLanguage = store.get("language");
 const translations =
 pickLocaleRecord(props.app.translationsByLocale, requestLocale) ??
 pickLocaleRecord(
 props.app.translationsByLocale,
 typeof stateLanguage === "string" ? stateLanguage : undefined,
 ) ??
 routeTranslations ??
 props.app.translations;
 const atomContext = useAtomContext({
 store, fireTrigger, setFormValue, getFormValues, translations, urlLocale: requestLocale,
 });

 const requestedLocale =
 typeof stateLanguage === "string" ? stateLanguage.trim() : "";
 const lastLocaleRequestRef = useRef<string | null>(null);
 useEffect(() => {
 if (!requestedLocale || !browserLocation) return;
 const locales = props.app.i18n?.locales ?? [];
 if (!locales.includes(requestedLocale)) return;

 const currentLocale = extractLocalePrefix(window.location.pathname, locales)?.locale;
 const needsLocalePath = currentLocale !== requestedLocale &&
 (currentLocale !== undefined || requestedLocale !== props.app.i18n?.defaultLocale);
 const hasBundle = Boolean(pickLocaleRecord(props.app.translationsByLocale, requestedLocale));
 if (!needsLocalePath && hasBundle) {
 lastLocaleRequestRef.current = null;
 return;
 }
 if (lastLocaleRequestRef.current === requestedLocale) return;
 lastLocaleRequestRef.current = requestedLocale;

 if (props.onActiveLocaleChange) {
 props.onActiveLocaleChange(requestedLocale);
 } else if (needsLocalePath) {
 const url = new URL(window.location.href);
 url.pathname = buildLocalePathname(url.pathname, requestedLocale, locales);
 window.location.assign(url.href);
 }
 }, [requestedLocale, browserLocation, props.app, props.onActiveLocaleChange]);

 useDocumentMetadataSync({
 app: props.app,
 screen,
 language:
 urlLocale ??
 (typeof stateLanguage === "string" && stateLanguage.trim()
 ? stateLanguage.trim()
 : requestLocale ?? props.activeLocale ?? props.app.i18n?.activeLocale),
 });

 useEffect(() => {
 if (typeof window === "undefined") return;
 const onInternalNavigate = () => {
 scrollToDocumentTop();
 };
 window.addEventListener(BROWSER_NAVIGATION_EVENT, onInternalNavigate);
 return () => {
 window.removeEventListener(BROWSER_NAVIGATION_EVENT, onInternalNavigate);
 };
 }, []);

 const browserLocationReady = Boolean(props.navigate) || browserLocation !== "";
 const pageKey = props.navigate
 ? `${screen?.manifest.id ?? ""}|${route.slug}|${stableKey(route.params)}|${stableKey(route.query)}`
 : browserLocation
 ? `${screen?.manifest.id ?? ""}|${browserLocation}`
 : "";

 const routeSnapshot = route as unknown as Record<string, unknown>;
 const routeMirrorKey = `${route.slug}|${stableKey(route.params)}|${stableKey(route.query)}`;
 const lastRouteMirrorKeyRef = useRef<string | null>(null);
 useEffect(() => {
 if (!browserLocationReady || lastRouteMirrorKeyRef.current === routeMirrorKey) return;
 lastRouteMirrorKeyRef.current = routeMirrorKey;
 store.set("route", routeSnapshot);
 }, [store, browserLocationReady, routeMirrorKey, routeSnapshot]);

 const languagePaths = useMemo(() => {
 const source = browserLocation || props.currentPathname || props.initialPathname;
 if (!source) return null;
 const pathname = source.split("?")[0] ?? "/";
 return buildLocalePathMap(pathname, props.app.i18n?.locales ?? []);
 }, [browserLocation, props.currentPathname, props.initialPathname, props.app]);
 useEffect(() => {
 if (!browserLocationReady || !languagePaths) return;
 store.set("languagePaths", languagePaths);
 }, [store, browserLocationReady, languagePaths]);

 usePrePaintEffect(() => {
 if (!browserLocationReady) return;
 const locale = typeof urlLocale === "string" && urlLocale ? urlLocale : "";
 if (store.get("urlLocale") !== locale) store.set("urlLocale", locale);
 if (!locale) return;
 if (store.get("language") !== locale) store.set("language", locale);
 const dir = resolveAppLocaleDir(props.app, locale);
 if (store.get("dir") !== dir) store.set("dir", dir);
 }, [browserLocationReady, urlLocale, store, props.app]);

 useScreenBootstrap({
 bootstrapKey: pageKey,
 screen,
 app: props.app,
 navigate,
 storage,
 auth,
 store,
 transport,
 logger,
 fireTrigger,
 });

 const scrollKey = props.navigate
 ? `${screen?.manifest.id ?? ""}|${route.slug}|${stableKey(route.params)}`
 : browserLocation
 ? `${screen?.manifest.id ?? ""}|${browserLocation.split("?")[0]}`
 : "";
 const lastScrollKeyRef = useRef<string | null>(null);
 usePrePaintEffect(() => {
 if (!browserLocationReady || !scrollKey) return;
 const previous = lastScrollKeyRef.current;
 lastScrollKeyRef.current = scrollKey;

 const isFirstDocumentMount = !navigationBookkeeping.hasCommittedScreen;
 navigationBookkeeping.hasCommittedScreen = true;

 const isHistoryNav =
 isHistoryNavigation ||
 Date.now() - navigationBookkeeping.lastPopStateAt < HISTORY_NAV_WINDOW_MS;

 if (previous !== null && previous !== scrollKey && !isHistoryNav) {
 scrollToDocumentTop();
 return;
 }

 if (
 previous === null &&
 !isFirstDocumentMount &&
 !isHistoryNav
 ) {
 scrollToDocumentTop();
 }
 }, [browserLocationReady, scrollKey, isHistoryNavigation]);

 if (!screen || !screen.ui) return null;
 return <div className="w-full min-w-0">{renderUiNode(screen.ui, atomContext)}</div>;
}